import { Component } from '@theme/component';

/**
 * Expands an inline predictive search field next to the header search icon.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} panel
 * @extends {Component<Refs>}
 */
class HeaderSearchInline extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {AbortController | null} */
  #outsideClickController = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.toggle.addEventListener('click', this.#toggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.toggle.removeEventListener('click', this.#toggle);
    this.#teardownOutsideClick();
  }

  #toggle = () => {
    if (this.classList.contains('is-open')) {
      this.#close();
    } else {
      this.#open();
    }
  };

  #open = () => {
    this.classList.add('is-open');
    this.refs.toggle.setAttribute('aria-expanded', 'true');

    const input = this.querySelector('input[type="search"]');
    if (input instanceof HTMLInputElement) {
      input.focus();
      input.dispatchEvent(new Event('focus', { bubbles: true }));
    }

    this.#outsideClickController = new AbortController();
    const { signal } = this.#outsideClickController;
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onDocumentKeydown, { signal });
  };

  #close = () => {
    this.classList.remove('is-open');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.#teardownOutsideClick();
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (!this.contains(target)) {
      this.#close();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#close();
      this.refs.toggle.focus();
    }
  };

  #teardownOutsideClick = () => {
    this.#outsideClickController?.abort();
    this.#outsideClickController = null;
  };
}

if (!customElements.get('header-search-inline')) {
  customElements.define('header-search-inline', HeaderSearchInline);
}
